import { DataKeys } from "../../types";

export type HealthLang = "En" | "Zh" | "Jp";
export type LocalizedString = Partial<Record<HealthLang, string>> & {
    En: string;
    Zh?: string;
    Jp?: string;
};

/** Product Suite audience groups (five on /health, including Educator bridge). */
export const AIMMA_PRODUCT_SUITE_GROUP_IDS = [
    "doctor",
    "patient",
    "nurse",
    "educator",
    "nursing-robot",
] as const;

export type AimmaProductSuiteGroupId =
    (typeof AIMMA_PRODUCT_SUITE_GROUP_IDS)[number];

/** Platform diagram audience nodes — care delivery only (no Educator). */
export const AIMMA_PRODUCT_GROUP_IDS = [
    "patient",
    "doctor",
    "nurse",
    "nursing-robot",
] as const;

export type AimmaProductGroupId = (typeof AIMMA_PRODUCT_GROUP_IDS)[number];

/** Product capability IDs — lifecycle chips and suite catalog. */
export const AIMMA_PRODUCT_IDS = [
    "aimma-diary",
    "aimma-consultation",
    "aimma-interview",
    "aimma-chronicle",
    "aimma-triage",
    "aimma-listening",
    "aimma-diagnosis",
    "aimma-treatment",
    "aimma-reminder",
    "aimma-reporting",
    "aimma-kiosk",
    "aimma-bedside",
    "aimma-notes",
    "aimma-nursing-robot",
    "aimma-sensei",
] as const;

export type AimmaProductId = (typeof AIMMA_PRODUCT_IDS)[number];

export type AimmaProductStatus =
    | "available"
    | "under-development"
    | "future-extension"
    | "planned-capability"
    | "strategic-extension";

export const LIFECYCLE_STAGE_COUNT = 5;

export const PRODUCT_SUITE_GROUP_COUNT = 5;

export function getLocalized(
    field: LocalizedString | undefined,
    lang: HealthLang
): string {
    if (!field) return "";
    return field[lang] ?? field.En ?? "";
}

export function isAimmaProductId(id: string): id is AimmaProductId {
    return (AIMMA_PRODUCT_IDS as readonly string[]).includes(id);
}

export const PRODUCT_CHIP_LABELS: Record<AimmaProductId, LocalizedString> = {
    "aimma-diary": {
        En: "Aimma Record",
        Zh: "Aimma 記錄",
        Jp: "Aimma 記録",
    },
    "aimma-consultation": {
        En: "Aimma Consult",
        Zh: "Aimma 諮詢",
        Jp: "Aimma 相談",
    },
    "aimma-interview": {
        En: "Aimma Interview",
        Zh: "Aimma 問診",
        Jp: "Aimma 問診",
    },
    "aimma-chronicle": {
        En: "Aimma Chronicle",
        Zh: "Aimma 病史紀要",
        Jp: "Aimma クロニクル",
    },
    "aimma-listening": {
        En: "Aimma Listening",
        Zh: "Aimma 問診助手",
        Jp: "Aimma 問診リスニング",
    },
    "aimma-triage": {
        En: "Aimma Triage",
        Zh: "Aimma 分診建議",
        Jp: "Aimma トリアージ",
    },
    "aimma-diagnosis": {
        En: "Aimma Diagnosis",
        Zh: "Aimma 診斷建議",
        Jp: "Aimma 診断支援",
    },
    "aimma-treatment": {
        En: "Aimma Treatment",
        Zh: "Aimma 治療計畫",
        Jp: "Aimma 治療計画",
    },
    "aimma-reminder": {
        En: "Aimma Reminder",
        Zh: "Aimma 覆核提醒",
        Jp: "Aimma リマインダー",
    },
    "aimma-reporting": {
        En: "Aimma Reporting",
        Zh: "Aimma 報告",
        Jp: "Aimma レポート",
    },
    "aimma-kiosk": {
        En: "Aimma Kiosk",
        Zh: "Aimma 諮詢站",
        Jp: "Aimma キオスク",
    },
    "aimma-bedside": {
        En: "Aimma Bedside",
        Zh: "Aimma 床邊助手",
        Jp: "Aimma ベッドサイド",
    },
    "aimma-notes": {
        En: "Aimma Notes",
        Zh: "Aimma 護理日誌",
        Jp: "Aimma 看護記録",
    },
    "aimma-nursing-robot": {
        En: "Aimma Nursing Robot",
        Zh: "Aimma 照護機器人",
        Jp: "Aimma 看護ロボット",
    },
    "aimma-sensei": {
        En: "Graphen Medical Sensei",
        Zh: "Graphen 醫學 Sensei",
        Jp: "Graphen 医学 Sensei",
    },
};

export const SUITE_GROUP_CHIP_LABELS: Record<
    AimmaProductSuiteGroupId,
    LocalizedString
> = {
    patient: {
        En: "Aimma for Patient",
        Zh: "Aimma 病人版",
        Jp: "Aimma 患者向け",
    },
    doctor: {
        En: "Aimma for Doctor",
        Zh: "Aimma 醫師版",
        Jp: "Aimma 医師向け",
    },
    nurse: {
        En: "Aimma for Nurse",
        Zh: "Aimma 護理版",
        Jp: "Aimma 看護向け",
    },
    educator: {
        En: "Aimma for Educator",
        Zh: "Aimma 教育版",
        Jp: "Aimma 教育向け",
    },
    "nursing-robot": {
        En: "Nursing Robot",
        Zh: "Aimma 照護機器人",
        Jp: "Aimma 看護ロボット",
    },
};

export function assertHealthLandingData(data: DataKeys): void {
    if (process.env.NODE_ENV === "production") return;

    const stages = data?.lifecycle?.stages;
    if (!Array.isArray(stages) || stages.length !== LIFECYCLE_STAGE_COUNT) {
        console.warn(
            `[Health] lifecycle.stages must have length ${LIFECYCLE_STAGE_COUNT}, got ${stages?.length}`
        );
    }

    stages?.forEach((stage: DataKeys) => {
        const related: string[] = stage?.relatedModules ?? [];
        related.forEach((id) => {
            if (!isAimmaProductId(id)) {
                console.warn(
                    `[Health] lifecycle stage "${stage?.id}" has invalid relatedModules id: ${id}`
                );
            }
        });
    });

    const groups = data?.productSuite?.groups;
    if (!Array.isArray(groups) || groups.length !== PRODUCT_SUITE_GROUP_COUNT) {
        console.warn(
            `[Health] productSuite.groups must have length ${PRODUCT_SUITE_GROUP_COUNT}, got ${groups?.length}`
        );
    }

    const retiredIds = [
        "aimma-mobile",
        "aimma-doctor",
        "aimma-nurse",
        "aimma-edu",
        "ai-core",
    ];
    const allRelated = stages?.flatMap((s: DataKeys) => s?.relatedModules ?? []) ?? [];
    allRelated.forEach((id: string) => {
        if (retiredIds.includes(id)) {
            console.warn(`[Health] retired product id in relatedModules: ${id}`);
        }
    });
}
